import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Col,
  Row,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { onError } from 'utils/handlers';
import Select from 'components/Select';
import { isMutable } from 'utils/estadosGlobales';
import makeMessager from 'utils/complexFormsMessages';
import { getConfiguracionesDeSegmentosDeFolios } from 'api/configuracion/configuracionesDeFolios';
import { getUsosDeFolios } from 'api/configuracion';
import {
  getConfiguracionesDeUsosDeFolios,
  patchConfiguracionDeUsosDeFolios,
  promiseAllUsosDeFolio,
} from 'api/configuracion/configuracionesDeUsoFolios';
import { getModelPermission } from 'utils/permission/index';

export const model = 'configuraciondeusodelfolio';
export const catalogsModels = ['usodepredio'];

const ConfiguracionDeFolios = ({
  setLoading,
  callback,
  setCurrentTabKey,
  setForm,
  disabled,
}) => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [folios, setFolios] = useState([]);
  const [usos, setUsos] = useState([]);
  const [usosPermission, setUsosPermission] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getConfiguracionesDeUsosDeFolios();
    const mappedData = _data.map((e) => ({ ...e, mutable: isMutable(e) }));
    setData(mappedData);
    if (mappedData?.length) {
      const entries = mappedData.map((e) => [e.uso_de_folio, e.configuracion_de_folio]);
      const values = Object.fromEntries(entries);
      form.setFieldsValue(values);
    }
    setLoading(false);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const keys = Object.keys(values);
      const promises = keys.map((e) => {
        const uso_de_folio = parseInt(e, 10);
        return { uso_de_folio, configuracion_de_folio: values[e] };
      });
      const results = await promiseAllUsosDeFolio(promises.filter((e) => e.configuracion_de_folio));
      if (results.length) {
        const _data = await getConfiguracionesDeUsosDeFolios();
        const mappedData = _data.map((e) => ({ ...e, mutable: isMutable(e) }));
        setData(mappedData);
        if (mappedData?.length) {
          const entries = mappedData.map((e) => [e.uso_de_folio, e.configuracion_de_folio]);
          const _values = Object.fromEntries(entries);
          form.setFieldsValue(_values);
        }
      }
      callback(_continue);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  const onFinishHandler = async (tabKey = null, _continue = true) => {
    try {
      if (tabKey) {
        showMsg(tabKey);
      } else {
        await onFinish(_continue);
      }
    } catch (err) {
      onError(err);
    }
  };

  useEffect(() => {
    let mounted = true;
    const permissionValidations = () => {
      const _usosPermission = getModelPermission(catalogsModels[0]);
      if (mounted) {
        setUsosPermission(_usosPermission);
      }
    };

    const fetch = async () => {
      setLoading(true);
      const _configs = await getConfiguracionesDeSegmentosDeFolios({ estados_globales: 4 });
      const _usosDeFolios = await getUsosDeFolios();
      await fetchData();
      if (mounted) {
        setFolios(_configs);
        setUsos(_usosDeFolios);
        setLoading(false);
      }
    };
    permissionValidations();
    fetch();
    setTimeout(() => {
      form.onFinishHandler = onFinishHandler;
      setForm(form);
    });
    return () => { mounted = false; };
  }, [disabled]);

  // const rules = {
  //   required: [{ required: !disabled }],
  // };

  const authorizeConfig = async (_values) => {
    setLoading(true);
    const values = { ..._values };
    values.estados_globales = 4;
    const authorized = await patchConfiguracionDeUsosDeFolios(values.id, values);
    if (authorized) {
      await fetchData();
    }
    setLoading(false);
  };

  return (
    <Col span={24}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ estados_globales: 1 }}
        name="folios"
      >
        <Row gutter={10}>
          {usos.map((i) => {
            const clone = [...data];
            const match = clone.find((e) => e.uso_de_folio === i.id
              && e.configuracion_de_folio === form.getFieldValue(`${i.id}`));
            return (
              <Col xs={24} sm={24} md={8} key={i.id}>
                <Form.Item
                  name={i.id}
                  // rules={rules.required}
                  label={i.nombre}
                >
                  <Select
                    dataSource={folios.map((e) => ({
                      ...e,
                      disabled: clone
                        .find((item) => item.configuracion_de_folio === e.id),
                    }))}
                    disabled={disabled || !isMutable(i)}
                  />
                </Form.Item>
                {(usosPermission?.authorize && match?.mutable && form.getFieldValue(`${i.id}`)) && (
                  <Button
                    onClick={() => authorizeConfig(match)}
                    style={{ width: '100%' }}
                    className="no-color success"
                    type="link"
                  >
                    <CheckCircleOutlined />
                    Autorizar
                  </Button>
                )}
              </Col>
            );
          })}
        </Row>
        <Form.Item hidden>
          <Button htmlType="submit" />
        </Form.Item>
      </Form>
    </Col>
  );
};

ConfiguracionDeFolios.propTypes = {
  callback: PropTypes.func,
  setLoading: PropTypes.func.isRequired,
  setForm: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  setCurrentTabKey: PropTypes.func.isRequired,
};

ConfiguracionDeFolios.defaultProps = {
  callback: () => { },
  disabled: false,
};

export default ConfiguracionDeFolios;
